/**
 * Firebase Configuration and Messaging Setup
 *
 * This module initializes Firebase and provides functions for:
 * - Getting FCM token for push notifications
 * - Handling foreground notifications
 *
 * Required environment variables (add to .env.local):
 * VITE_FIREBASE_API_KEY=your-api-key
 * VITE_FIREBASE_AUTH_DOMAIN=toss-ozkvwo.firebaseapp.com
 * VITE_FIREBASE_PROJECT_ID=toss-ozkvwo
 * VITE_FIREBASE_STORAGE_BUCKET=toss-ozkvwo.appspot.com
 * VITE_FIREBASE_MESSAGING_SENDER_ID=your-sender-id
 * VITE_FIREBASE_APP_ID=your-app-id
 * VITE_FIREBASE_VAPID_KEY=your-vapid-key
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  onMessage,
  type Messaging,
  type MessagePayload,
} from 'firebase/messaging';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'toss-ozkvwo.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'toss-ozkvwo',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'toss-ozkvwo.appspot.com',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// VAPID key for web push notifications
const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;

/**
 * Check if Firebase is properly configured
 */
export function isFirebaseConfigured(): boolean {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.messagingSenderId &&
    firebaseConfig.appId &&
    vapidKey
  );
}

/**
 * Initialize Firebase app
 */
function initializeFirebase(): FirebaseApp | null {
  if (app) return app;

  if (!isFirebaseConfigured()) {
    console.warn(
      'Firebase is not configured. Push notifications will be disabled. ' +
        'Please add VITE_FIREBASE_* environment variables.'
    );
    return null;
  }

  try {
    app = initializeApp(firebaseConfig);
    console.log('Firebase initialized successfully');
    return app;
  } catch (error) {
    console.error('Failed to initialize Firebase:', error);
    return null;
  }
}

/**
 * Get Firebase Messaging instance
 */
function getMessagingInstance(): Messaging | null {
  if (messaging) return messaging;

  const firebaseApp = initializeFirebase();
  if (!firebaseApp) return null;

  // Check if browser supports notifications
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return null;
  }

  try {
    messaging = getMessaging(firebaseApp);
    return messaging;
  } catch (error) {
    console.error('Failed to get messaging instance:', error);
    return null;
  }
}

/**
 * Request notification permission and get FCM token.
 *
 * Registers the Firebase messaging service worker lazily (on first call)
 * and waits for it to reach the `activated` state before requesting a token.
 * This is critical on Android PWA — without waiting, `getToken` can race
 * the SW activation and return null.
 *
 * @returns FCM token or null if permission denied or error
 */
export async function getMessagingToken(): Promise<string | null> {
  const messagingInstance = getMessagingInstance();
  if (!messagingInstance) {
    return null;
  }

  try {
    // Check current permission status
    if (Notification.permission === 'denied') {
      console.warn('Notification permission was denied');
      return null;
    }

    // Request permission if not granted
    if (Notification.permission !== 'granted') {
      const result = await Notification.requestPermission();
      if (result !== 'granted') {
        console.warn('Notification permission not granted');
        return null;
      }
    }

    // Lazy register Firebase SW (separate scope from PWA SW).
    // Registering lazily eliminates the race condition where getToken()
    // runs before the SW has finished activating on Android PWA.
    const registration = await navigator.serviceWorker.register(
      '/firebase-messaging-sw.js',
      { scope: '/firebase-cloud-messaging-push-scope' }
    );

    // Wait until the SW reaches `activated` state. If it's already active,
    // both `installing` and `waiting` will be null and we skip the wait.
    if (registration.installing || registration.waiting) {
      await new Promise<void>((resolve) => {
        const sw = registration.installing ?? registration.waiting;
        if (!sw) {
          resolve();
          return;
        }
        const onStateChange = (): void => {
          if (sw.state === 'activated') {
            sw.removeEventListener('statechange', onStateChange);
            resolve();
          }
        };
        sw.addEventListener('statechange', onStateChange);
      });
    }

    const token = await getToken(messagingInstance, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      console.log('FCM token obtained successfully');
      return token;
    }

    console.warn('No FCM token available');
    return null;
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
}

/**
 * Set up foreground message handler
 * Called when the app is in the foreground and receives a push notification
 * @param callback Function to handle the notification payload
 * @returns Unsubscribe function
 */
export function onForegroundMessage(
  callback: (payload: MessagePayload) => void
): (() => void) | null {
  const messagingInstance = getMessagingInstance();
  if (!messagingInstance) {
    return null;
  }

  return onMessage(messagingInstance, (payload) => {
    console.log('Foreground message received:', payload);
    callback(payload);
  });
}

/**
 * Show a browser notification (for foreground messages)
 */
export function showBrowserNotification(
  title: string,
  options?: NotificationOptions
): void {
  if (Notification.permission === 'granted') {
    new Notification(title, {
      icon: '/icons/notification-icon.png',
      badge: '/icons/badge-icon.png',
      ...options,
    });
  }
}
